import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Copy, QrCode, ExternalLink, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { getOrderDetail, checkOrderStatus, retryOrder, payOrder, fetchPaymentMethods, downloadInvoice, OrderDetail, DuitkuMethod } from '../../features/orders/api/orders';
import { firePurchasePixel } from '../../features/analytics/purchasePixel';
import { formatPrice } from '../../features/courses/api/courses';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Countdown } from '../../components/common/Countdown';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Download } from 'lucide-react';

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [methods, setMethods] = useState<DuitkuMethod[]>([]);
  const [pickOpen, setPickOpen] = useState(false);
  const [selected, setSelected] = useState('');

  const load = useCallback(() => {
    if (!id) return;
    getOrderDetail(id).then(setDetail).finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);

  const order = detail?.order;
  const pay = detail?.payment;
  const isPending = order?.status === 'pending';

  // poll while pending
  useEffect(() => {
    if (!id || !isPending) return;
    const t = setInterval(async () => {
      try {
        const o = await checkOrderStatus(id);
        if (o.status !== order?.status) {
          load();
          if (o.status === 'paid') {
            toast.success('Pembayaran diterima!');
            firePurchasePixel(id, order?.amount || 0, order?.currency || 'IDR');
          }
        }
      } catch {}
    }, 7000);
    return () => clearInterval(t);
  }, [id, isPending, order?.status, order?.amount, order?.currency, load]);

  // Also fire once when landing on an already-paid order (Duitku return page).
  useEffect(() => {
    if (order?.status === 'paid' && id) {
      firePurchasePixel(id, order.amount, order.currency || 'IDR');
    }
  }, [order?.status, id, order?.amount, order?.currency]);

  const copyText = (t: string) => { navigator.clipboard.writeText(t); toast.success('Disalin'); };

  const getInvoice = async () => {
    if (!id) return;
    try {
      const blob = await downloadInvoice(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${order?.order_code || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) { toast.error('Gagal mengunduh invoice'); }
  };

  const startRetry = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await retryOrder(id);
      const res = await fetchPaymentMethods(order!.amount).catch(() => ({ mode: 'mock', methods: [], message: '' } as any));
      const real = res.methods.filter((m: DuitkuMethod) => m.paymentMethod !== 'MOCK');
      if (res.mode === 'duitku' && real.length > 0) { setMethods(real); setSelected(real[0].paymentMethod); setPickOpen(true); }
      else { const p = await payOrder(id); if (p.checkout_url) window.location.href = p.checkout_url.startsWith('http') ? p.checkout_url : `${process.env.REACT_APP_BACKEND_URL}${p.checkout_url}`; }
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const confirmPay = async () => {
    if (!id) return;
    setBusy(true);
    try { await payOrder(id, selected); setPickOpen(false); load(); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Detail Pesanan">
      <div className="max-w-xl" data-testid="order-detail-page">
        <button onClick={() => navigate('/member/orders')} className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-400 mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Kembali ke Orders
        </button>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : !order ? (
          <p className="text-white/50 py-20 text-center">Pesanan tidak ditemukan.</p>
        ) : (
          <div className="space-y-5">
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{order.product_title || order.product_type}</p>
                  <p className="text-xs text-white/40 font-mono mt-0.5">{order.order_code}</p>
                </div>
                <StatusBadge status={order.status} />
              </div>
              <p className="text-2xl font-light text-cyan-400 mt-4">{formatPrice(order.amount, order.currency)}</p>
            </div>

            {order.status === 'paid' && (
              <div className="bg-emerald-400/[0.06] border border-emerald-400/30 rounded-2xl p-6 text-center space-y-3">
                <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto" strokeWidth={1.5} />
                <p className="text-emerald-300 text-sm">Pembayaran lunas. Course Anda aktif.</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <button data-testid="order-open-course" onClick={() => navigate('/member/courses')} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg hover:bg-cyan-300 transition-colors text-sm">Buka My Courses</button>
                  <button data-testid="order-invoice" onClick={getInvoice} className="border border-white/15 text-white px-6 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-sm flex items-center gap-2"><Download className="w-4 h-4" strokeWidth={1.5} /> Unduh Invoice</button>
                </div>
              </div>
            )}

            {isPending && pay && (pay.va_number || pay.qr_string) && (
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="order-payment-instruction">
                <p className="text-sm uppercase tracking-wider text-white/70">Instruksi Pembayaran</p>
                {pay.expired_at && (
                  <div className="flex items-center gap-2 text-xs text-amber-300"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Bayar sebelum <Countdown expiresAt={pay.expired_at} onExpire={load} /></div>
                )}
                {pay.qr_string ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-2 text-sm text-white/70"><QrCode className="w-4 h-4 text-cyan-400" strokeWidth={1.5} /> Scan QRIS</div>
                    <div className="bg-white p-3 rounded-xl" data-testid="order-qris"><QRCodeSVG value={pay.qr_string} size={190} /></div>
                  </div>
                ) : pay.va_number ? (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/50 mb-1.5">Nomor Virtual Account</p>
                    <div className="flex items-center gap-2">
                      <div data-testid="order-va-number" className="flex-1 bg-[#050914] border border-white/10 rounded-lg px-4 py-3 font-mono text-lg text-white">{pay.va_number}</div>
                      <button onClick={() => copyText(pay.va_number!)} className="p-3 rounded-lg border border-white/10 text-white/60 hover:text-cyan-400 hover:bg-white/5"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </div>
                ) : null}
                {pay.checkout_url && (
                  <a href={pay.checkout_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300"><ExternalLink className="w-4 h-4" strokeWidth={1.5} /> Buka halaman Duitku</a>
                )}
              </div>
            )}

            {(order.status === 'failed' || order.status === 'expired') && (
              <button data-testid="order-retry" onClick={startRetry} disabled={busy} className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg hover:bg-cyan-300 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <RefreshCw className="w-4 h-4" strokeWidth={2} />} Coba Bayar Lagi
              </button>
            )}
          </div>
        )}
      </div>

      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-md max-h-[80vh] overflow-y-auto" data-testid="order-method-dialog">
          <DialogHeader><DialogTitle>Pilih Metode Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {methods.map((m) => (
              <button key={m.paymentMethod} data-testid={`order-method-${m.paymentMethod}`} onClick={() => setSelected(m.paymentMethod)} className={`w-full flex items-center gap-3 rounded-xl p-3 border text-left transition-colors ${selected === m.paymentMethod ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/10 hover:border-white/30'}`}>
                {m.paymentImage ? <img src={m.paymentImage} alt="" className="w-10 h-7 object-contain bg-white rounded p-0.5" /> : <div className="w-10 h-7 rounded bg-white/10" />}
                <span className="text-sm flex-1 truncate">{m.paymentName}</span>
                {selected === m.paymentMethod && <span className="w-2.5 h-2.5 rounded-full bg-cyan-400" />}
              </button>
            ))}
          </div>
          <button data-testid="order-method-confirm" onClick={confirmPay} disabled={busy || !selected} className="mt-2 w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg hover:bg-cyan-300 disabled:opacity-60 flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Lanjut Bayar</button>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
